import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useMemo, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useUrlSort } from "../../../hooks/useUrlSort";
import { useMakeUrl, navigate, routes } from "../../../lib/urls";
import { userDisplayName } from "../../../lib/userDisplayName";
import { Banner } from "../../Banner";
import { Button } from "../../Button";
import { ButtonLink } from "../../Button/ButtonLink";
import { ConfirmModal } from "../../ConfirmModal";
import { Dropdown } from "../../Dropdown";
import { Icon } from "../../Icon";
import { ListTable } from "../../ListTable";
import { SpaceBadge } from "../../SpaceBadge";
import { Text } from "../../Text";
import {
  Container,
  Header,
  HoverActions,
  NeutralIconBadge,
  RowNameContent,
  RowNameLayout,
  ScrollArea,
} from "../shared";

const BannerActionButton = styled(ButtonLink)`
  && {
    background: var(--orange-400);
    color: var(--orange-1000);
  }
  &&:hover:not(:disabled) {
    background: color-mix(in oklab, var(--orange-400), var(--orange-600) 20%);
  }
`;

export function ApiKeysSettings() {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const [apiKeys, apiKeysResult] = useQuery(queries.apiKeys());
  const canSeeCreators = true;
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.settings.apiKeys,
    {
      name: "asc",
      access: "asc",
      createdBy: "asc",
      created: "desc",
      lastUsed: "desc",
    }
  );

  const noKeys = apiKeysResult.type === "complete" && apiKeys.length === 0;
  useEffect(() => {
    if (noKeys) {
      navigate(routes.settings.preferences, undefined, { replace: true });
    }
  }, [noKeys]);

  const items = useMemo(() => {
    const sorted = [...apiKeys].sort((a, b) => {
      if (!sortColumn) return 0;

      let aVal: string | number | null = null;
      let bVal: string | number | null = null;

      if (sortColumn === "name") {
        aVal = a.name ?? "";
        bVal = b.name ?? "";
      } else if (sortColumn === "access") {
        aVal = a.space?.name ?? "";
        bVal = b.space?.name ?? "";
      } else if (sortColumn === "createdBy") {
        aVal = a.user ? userDisplayName(a.user) : "";
        bVal = b.user ? userDisplayName(b.user) : "";
      } else if (sortColumn === "created") {
        aVal = a.createdAt ?? 0;
        bVal = b.createdAt ?? 0;
      } else if (sortColumn === "lastUsed") {
        aVal = a.lastRequest ?? 0;
        bVal = b.lastRequest ?? 0;
      }

      if (aVal === null || bVal === null) return 0;

      let comparison = 0;
      if (typeof aVal === "string" && typeof bVal === "string") {
        comparison = aVal.localeCompare(bVal);
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted.map((key) => ({
      key: key.id,
      link: "",
      name: (
        <RowNameLayout>
          <NeutralIconBadge iconName="key" />
          <RowNameContent>
            <Text variant="text-regular-md" ellipsis>
              {key.name}
            </Text>
            <Text variant="text-small" color="var(--text-secondary)">
              {key.start ? `${key.start}…` : ""}
            </Text>
          </RowNameContent>
        </RowNameLayout>
      ),
      access: key.space ? (
        <SpaceBadge space={key.space} />
      ) : (
        <Text variant="text-small" color="var(--text-secondary)">
          All spaces
        </Text>
      ),
      createdBy: (
        <Text variant="text-small" ellipsis>
          {key.user ? userDisplayName(key.user) : ""}
        </Text>
      ),
      created: (
        <Text variant="text-small">
          {key.createdAt != null
            ? new Date(key.createdAt).toLocaleDateString()
            : ""}
        </Text>
      ),
      lastUsed: (
        <Text variant="text-small">
          {key.lastRequest != null
            ? new Date(key.lastRequest).toLocaleDateString()
            : "Never"}
        </Text>
      ),
      actions: (hasMouse: boolean) => (
        <HoverActions visible={hasMouse}>
          <Dropdown
            opener={<Button variant="secondary" icon="more" />}
            align="bottom"
            minWidth={160}
            sections={[
              {
                items: [
                  {
                    type: "button" as const,
                    left: <Icon name="trash" size={16} />,
                    label: "Delete",
                    onClick: () =>
                      setConfirmDelete({
                        id: key.id,
                        name: key.name ?? "API key",
                      }),
                  },
                ],
              },
            ]}
          />
        </HoverActions>
      ),
    }));
  }, [apiKeys, sortColumn, sortDirection]);

  if (noKeys) return null;

  return (
    <Container>
      <Header>
        <div style={{ flex: 1 }}>
          <Text variant="text-regular-md">API keys</Text>
        </div>
        <div style={{ minWidth: 0 }}>
          <Banner
            actions={
              <BannerActionButton
                variant="secondary"
                to={makeUrl(routes.settings.members.serviceAccounts)}
              >
                Go to service accounts
              </BannerActionButton>
            }
          >
            API keys are deprecated — new keys can’t be created.
          </Banner>
        </div>
        <div style={{ flex: 1 }} />
      </Header>

      <ScrollArea>
        {apiKeys.length > 0 && (
          <ListTable
            headers={{
              name: "Name",
              access: "Access",
              ...(canSeeCreators ? { createdBy: "Created by" } : {}),
              created: "Created",
              lastUsed: "Last used",
              actions: "",
            }}
            columns={{}}
            sortableColumns={sortableColumns}
            dateColumns={["created", "lastUsed"]}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            items={items}
          />
        )}
      </ScrollArea>

      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            z.mutate(sharedMutators.deleteApiKey({ id: confirmDelete.id }));
            setConfirmDelete(null);
          }}
          title="Delete API key"
          description={`“${confirmDelete.name}” will stop working immediately. This can’t be undone.`}
          confirmLabel="Delete key"
          destructive
        />
      )}
    </Container>
  );
}
